import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { ArrowLeft, ChevronRight, Menu, Plus, Server, Users } from 'lucide-react';
import type { MinecraftServer } from '@spawnwire/shared';
import { Button, clsx, EmptyState, IconButton, Spinner } from '../../components/ui';
import { attempt } from '../../lib/data';
import { onReady } from '../../lib/gateway';
import { useIsMobile } from '../../lib/hooks';
import { ui } from '../../store/ui';
import { CreateMinecraftServer, isTransitional, MinecraftServerCard, STATE_LABEL, STATE_TONE } from '../minecraft/MinecraftPanels';
import { minecraft, useMinecraft } from '../minecraft/store';
import { GrassBlockIcon } from './GameIcons';

/** Games people can deploy. Add new games here; each brings its own deploy page and limit. */
const GAMES = [
  {
    id: 'minecraft',
    name: 'Minecraft: Java Edition',
    description: 'Vanilla or Paper, any version. 2 GB RAM and 5 GB storage.',
    icon: GrassBlockIcon,
  },
] as const;

function BackLink({ to, children }: { to: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => navigate(to)} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg">
      <ArrowLeft className="size-4" aria-hidden /> {children}
    </button>
  );
}

function Loading() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Spinner className="size-8" />
    </div>
  );
}

function StatusPill({ server }: { server: MinecraftServer }) {
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted">
      <span className={clsx('size-2 rounded-full', STATE_TONE[server.state], isTransitional(server.state) && 'animate-pulse')} aria-hidden />
      {STATE_LABEL[server.state]}
    </span>
  );
}

/** Why game hosting on Spawnwire is free, and who provides it. */
function HostingBanner() {
  return (
    <section className="mb-6 rounded-xl border border-line bg-sidebar p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <span className="hidden size-14 shrink-0 items-center justify-center rounded-xl bg-main/70 sm:flex">
          <Server className="size-7 text-muted" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-fg sm:text-xl">Host a server with your friends, completely free</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted">
            At Spawnwire we want gamers and friends to connect and play together without paying a thing, with little to no ads. Spin up a server in a minute, invite your friends and play.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-main/70 px-3 py-1 text-xs font-medium text-fg">
            <Server className="size-3.5 text-accent" aria-hidden /> All hosting is provided by{' '}
            <a href="https://craftnodes.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-accent hover:underline">
              CraftNodes
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

function ServerList() {
  const overview = useMinecraft((s) => s.overview);
  const error = useMinecraft((s) => s.error);
  if (!overview) {
    return error ? <EmptyState icon={Server} title="Could not load your servers" body={error} action={<Button onClick={() => void attempt(() => minecraft.load())}>Try again</Button>} /> : <Loading />;
  }
  return (
    <div className="mx-auto w-full max-w-3xl">
      <HostingBanner />
      <ServerRows servers={overview.servers} />
    </div>
  );
}

function ServerRows({ servers }: { servers: MinecraftServer[] }) {
  const navigate = useNavigate();
  if (servers.length === 0) {
    return (
      <EmptyState
        icon={Server}
        title="No servers yet"
        body="Deploy a free game server and play with your friends. It turns itself off when nobody is online."
        action={
          <Button icon={Plus} onClick={() => navigate('deploy')}>
            Deploy server
          </Button>
        }
      />
    );
  }
  return (
    <div>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">Your servers — {servers.length}</h2>
      <ul className="space-y-2">
        {servers.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => navigate(s.id)}
              className="flex w-full items-center gap-4 rounded-xl border border-line bg-sidebar p-4 text-left transition-colors hover:bg-elevated"
            >
              <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-main">
                <GrassBlockIcon size={30} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-fg">{s.name}</span>
                <span className="block truncate text-sm text-muted">
                  Minecraft · {s.software === 'paper' ? 'Paper' : 'Vanilla'} {s.version} · <span className="font-mono">{s.address}</span>
                </span>
              </span>
              {s.players && (
                <span className="hidden items-center gap-1 text-sm text-muted sm:flex">
                  <Users className="size-4" aria-hidden /> {s.players.online}/{s.players.max}
                </span>
              )}
              <StatusPill server={s} />
              <ChevronRight className="size-5 shrink-0 text-faint" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GamePicker() {
  const navigate = useNavigate();
  const overview = useMinecraft((s) => s.overview);
  if (!overview) return <Loading />;
  const limit = overview.limits?.maxServers ?? 0;
  const owned = overview.servers.length;
  const existing = overview.servers[0];
  return (
    <div className="mx-auto w-full max-w-3xl">
      <BackLink to="/app/servers">Your servers</BackLink>
      <h2 className="text-xl font-bold text-fg">Deploy a server</h2>
      <p className="mt-1 text-sm text-muted">Choose a game.</p>
      <ul className="mt-5 space-y-2">
        {GAMES.map((game) => {
          const available = overview.enabled && owned < limit;
          const reason = !overview.enabled ? 'Not available on this Spawnwire server yet.' : `You already have ${limit === 1 ? 'a' : limit} ${game.name.split(':')[0]} server${limit === 1 ? '' : 's'}.`;
          return (
            <li key={game.id}>
              <div className={clsx('flex items-center gap-4 rounded-xl border border-line bg-sidebar p-4', available && 'transition-colors hover:bg-elevated')}>
                <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-main">
                  <game.icon size={36} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-fg">{game.name}</span>
                  <span className="block text-sm text-muted">{game.description}</span>
                  <span className={clsx('mt-1 block text-xs', available ? 'text-faint' : 'text-warning')}>{available ? `Limit: ${limit} server${limit === 1 ? '' : 's'} per person` : reason}</span>
                </span>
                {available ? (
                  <Button onClick={() => navigate(`/app/servers/deploy/${game.id}`)}>Deploy</Button>
                ) : existing && overview.enabled ? (
                  <Button variant="secondary" onClick={() => navigate(`/app/servers/${existing.id}`)}>
                    Open yours
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
        <li className="rounded-xl border border-dashed border-line p-4 text-center text-sm text-faint">More games are coming soon.</li>
      </ul>
    </div>
  );
}

function DeployMinecraft() {
  const navigate = useNavigate();
  const overview = useMinecraft((s) => s.overview);
  if (!overview) return <Loading />;
  if (!overview.enabled || !overview.limits || overview.servers.length >= overview.limits.maxServers) return <Navigate to="/app/servers/deploy" replace />;
  return (
    <div className="mx-auto w-full max-w-xl">
      <BackLink to="/app/servers/deploy">Choose a different game</BackLink>
      <CreateMinecraftServer idleSeconds={overview.limits.idleStopSeconds} onCreated={(s) => navigate(`/app/servers/${s.id}`, { replace: true })} />
    </div>
  );
}

function ServerDetail() {
  const { serverId = '' } = useParams();
  const navigate = useNavigate();
  const overview = useMinecraft((s) => s.overview);
  if (!overview) return <Loading />;
  const server = overview.servers.find((s) => s.id === serverId);
  if (!server || !overview.limits) return <Navigate to="/app/servers" replace />;
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col">
      <BackLink to="/app/servers">Your servers</BackLink>
      <MinecraftServerCard server={server} limits={overview.limits} onDeleted={() => navigate('/app/servers', { replace: true })} />
    </div>
  );
}

/** Game servers people host on Spawnwire: their list, the game picker, deploy forms and each server's page. */
export function ServersView() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const overview = useMinecraft((s) => s.overview);

  useEffect(() => {
    void minecraft.load();
    const off = onReady(() => void minecraft.load());
    // Status also arrives over the socket; this catches anything missed while the tab slept.
    const poll = setInterval(() => void minecraft.load(), 30_000);
    return () => {
      off();
      clearInterval(poll);
    };
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
        {isMobile && <IconButton icon={Menu} label="Open navigation" onClick={() => ui.setMobilePane('nav')} />}
        <span className="flex items-center gap-2 font-semibold text-fg">
          <Server className="size-5 text-muted" aria-hidden /> Game Servers
        </span>
        {overview?.enabled && overview.servers.length > 0 && (
          <Button size="sm" icon={Plus} className="ml-auto" onClick={() => navigate('/app/servers/deploy')}>
            Deploy server
          </Button>
        )}
      </header>
      <div className="scroll-thin flex min-h-0 flex-1 flex-col overflow-y-auto p-4 sm:p-6">
        {overview && !overview.enabled ? (
          <EmptyState icon={Server} title="Game servers are not available" body="This Spawnwire server is not set up to host game servers yet." />
        ) : (
          <Routes>
            <Route index element={<ServerList />} />
            <Route path="deploy" element={<GamePicker />} />
            <Route path="deploy/minecraft" element={<DeployMinecraft />} />
            <Route path=":serverId" element={<ServerDetail />} />
            <Route path="*" element={<Navigate to="/app/servers" replace />} />
          </Routes>
        )}
      </div>
    </div>
  );
}
